import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { OfflineProvider } from "./contexts/OfflineContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { InstallBanner } from "./components/ui/InstallBanner";

// Layouts
import MainLayout from "./components/layouts/MainLayout";
import AuthLayout from "./components/layouts/AuthLayout";

// Auth pages
import LoginPage from "./pages/auth/LoginPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import AcceptInvitePage from "./pages/auth/AcceptInvitePage";
import AuthCallback from "./pages/auth/AuthCallback";
import OnboardingPage from "./pages/auth/OnboardingPage";
import ConfirmEmailPage from "./pages/auth/ConfirmEmailPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import VerifyPersonalEmailPage from "./pages/auth/VerifyPersonalEmailPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";

// Dashboard pages
import AdminDashboard from "./pages/dashboard/AdminDashboard";
import AgentDashboard from "./pages/dashboard/AgentDashboard";
import AgentCockpitPage from "./pages/dashboard/AgentCockpitPage";
import SellerDashboard from "./pages/dashboard/SellerDashboard";

// Mandate pages
import MandatesPage from "./pages/mandates/MandatesPage";
import MandateDetailPage from "./pages/mandates/MandateDetailPage";
import MandateDetailV2Page from "./pages/mandates/MandateDetailV2Page";
import MandateConsultationPage from "./pages/mandates/MandateConsultationPage";
import AmendmentConsultationPage from "./pages/mandates/AmendmentConsultationPage";
import MandateUploadPage from "./pages/mandates/MandateUploadPage";
import MyUploadsPage from "./pages/mandates/MyUploadsPage";
import SellerOnboardingPage from "./pages/seller/SellerOnboardingPage";
import MandateStatsPage from "./pages/mandates/MandateStatsPage";
import EstimationsPage from "./pages/estimations/EstimationsPage";
import KPICatalogPage from "./pages/kpi/KPICatalogPage";
import AIFullReportPage from "./pages/AIFullReportPage";
import AIInsightsPage from "./pages/AIInsightsPage";
import PourLesAgencesPage from "./pages/PourLesAgencesPage";
import PourLesAgentsPage from "./pages/PourLesAgentsPage";
import TargetsPage from "./pages/targets/TargetsPage";
import PilotagePage from "./pages/admin/PilotagePage";
import SimulatorPage from "./pages/simulator/SimulatorPage";
import SharedDiagnosticPage from "./pages/simulator/SharedDiagnosticPage";

// Team pages
import TeamPage from "./pages/team/TeamPage";

// Billing pages
import BillingPage from "./pages/billing/BillingPage";
import QuickstartPage from "./pages/quickstart/QuickstartPage";
import AIInsightsPreviewPage from "./pages/preview/AIInsightsPreviewPage";

// Settings pages
import SettingsPage from "./pages/settings/SettingsPage";

// Notifications
import NotificationsPage from "./pages/notifications/NotificationsPage";

// Legal pages
import CGUPage from "./pages/legal/CGUPage";
import PrivacyPage from "./pages/legal/PrivacyPage";
import CookiesPage from "./pages/legal/CookiesPage";
import MentionsLegalesPage from "./pages/legal/MentionsLegalesPage";

// Super Admin
import SuperAdminPage from "./pages/admin/SuperAdminPage";
import SuperAdminStatsPage from "./pages/admin/SuperAdminStatsPage";
import QAPanel from "./pages/qa/QAPanel";
import EmailLogsPage from "./pages/super-admin/EmailLogsPage";

// Admin Statistics
import AgencyStatsPage from "./pages/admin/AgencyStatsPage";

// Leaderboard
import LeaderboardPage from "./pages/leaderboard/LeaderboardPage";

// Rating pages
import RatingPage from "./pages/ratings/RatingPage";
import ConfirmRatingPage from "./pages/ratings/ConfirmRatingPage";

// Landing
import LandingPage from "./pages/LandingPage";
import RoadmapPage from "./pages/RoadmapPage";
import DiagnosticPage from "./pages/DiagnosticPage";
import WelcomePage from "./pages/WelcomePage";

// Blog
import BlogPage from "./pages/blog/BlogPage";
import BlogArticlePage from "./pages/blog/BlogArticlePage";

// Public Profiles
import RecherchePage from "./pages/public/RecherchePage";

// Réseau Homly
import ReseauLandingPage from "./pages/reseau/ReseauLandingPage";
import ReseauCockpitHubPage from "./pages/reseau/ReseauCockpitHubPage";
import NotaryDossiersListPage from "./pages/notaire/NotaryDossiersListPage";
import NotaryDossierDetailPage from "./pages/notaire/NotaryDossierDetailPage";
import NotaryPublicViewPage from "./pages/notaire/NotaryPublicViewPage";
import CommissionsPage from "./pages/commissions/CommissionsPage";
import CommissionGridsPage from "./pages/commissions/CommissionGridsPage";
import ReseauProfilPage from "./pages/reseau/ReseauProfilPage";
import ReseauStatistiquesPage from "./pages/reseau/ReseauStatistiquesPage";
import ReseauParametresPage from "./pages/reseau/ReseauParametresPage";
import ReseauSearchPage from "./pages/reseau/ReseauSearchPage";
import ReseauFeedPage from "./pages/reseau/ReseauFeedPage";
import ReseauMyPostsPage from "./pages/reseau/ReseauMyPostsPage";
import ReseauMyProofsPage from "./pages/reseau/ReseauMyProofsPage";
import ReseauSellerReviewsPage from "./pages/reseau/ReseauSellerReviewsPage";
import ReseauSellerIntentsPage from "./pages/reseau/ReseauSellerIntentsPage";
import PublicSellerReviewForm from "./pages/PublicSellerReviewForm";
import ReseauNotificationsPage from "./pages/reseau/ReseauNotificationsPage";
import PublicAgentProfilePage from "./pages/reseau/PublicAgentProfilePage";
import PublicAgencyPage from "./pages/public/PublicAgencyPage";
import RespondProposalPage from "./pages/reseau/RespondProposalPage";

// Reputation System
import AgentReputationPage from "./pages/reputation/AgentReputationPage";
import ShareStudioPage from "./pages/agent/ShareStudioPage";
import InviteReviewPage from "./pages/agent/InviteReviewPage";
import PublicReviewPage from "./pages/public/PublicReviewPage";
import RegisterSoloPage from "./pages/auth/RegisterSoloPage";
import SoloLandingPage from "./pages/landing/SoloLandingPage";
import SellerFeedbackPage from "./pages/reputation/SellerFeedbackPage";
import CertificationsPage from "./pages/reputation/CertificationsPage";

// Profile
import ProfilePage from "./pages/profile/ProfilePage";

// Support
import SupportPage from "./pages/support/SupportPage";

// Admin
import AgencyDashboard from "./pages/admin/AgencyDashboard";

// Offers
import OfferSignPage from "./pages/offers/OfferSignPage";
import BuyerOfferPublicPage from "./pages/offers/BuyerOfferPublicPage";
import OffersManagement from "./pages/offers/OffersManagement";

// Internal Dashboard (Homly Enterprise)
import InternalDashboard from "./pages/internal/InternalDashboard";
import DesignKitPage from "./pages/internal/DesignKitPage";

// Agency Onboarding
import AgencyOnboardingPage from "./pages/onboarding/AgencyOnboardingPage";

// 404 Page
import NotFoundPage from "./pages/NotFoundPage";
import HomlyProPage from "./pages/professionals/HomlyProPage";
import ProRegistrationPage from "./pages/professionals/ProRegistrationPage";
import ProLoginPage from "./pages/professionals/ProLoginPage";
import ProDashboardPage from "./pages/professionals/ProDashboardPage";
import ProReviewPage from "./pages/professionals/ProReviewPage";

// Messaging
import MessagingPage from "./pages/messaging/MessagingPage";

// Group Dashboard (multi-agences)
import GroupDashboardPage from "./pages/group/GroupDashboardPage";

// Visit Reports
import VisitFormPage from "./pages/visits/VisitFormPage";
import BonVisiteSignPage from "./pages/visits/BonVisiteSignPage";
import DataProtectionPage from "./pages/DataProtectionPage";

// Protected Route Component
function ProtectedRoute({ children, allowedRoles = [] }) {
    const { isAuthenticated, user, loading } = useAuth();
    const location = useLocation();

    // Skip auth check if returning from OAuth
    if (location.state?.user) {
        return children;
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="spinner w-8 h-8"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Check if onboarding is needed
    if (!user?.onboarding_complete && !user?.role) {
        return <Navigate to="/onboarding" replace />;
    }

    if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
        // Redirect to appropriate dashboard based on role
        if (user?.role === 'super_admin') return <Navigate to="/homly-internal" replace />;
        if (user?.role === 'group_manager') return <Navigate to="/group" replace />;
        if (user?.role === 'admin') return <Navigate to="/admin" replace />;
        if (user?.role === 'agent') return <Navigate to="/agent" replace />;
        if (user?.role === 'seller') return <Navigate to="/seller" replace />;
        return <Navigate to="/login" replace />;
    }

    return children;
}

// Dashboard redirect based on role
function DashboardRedirect() {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="spinner w-8 h-8"></div>
            </div>
        );
    }

    // Check if returning from OAuth with session_id
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    if (!user?.role) {
        return <Navigate to="/onboarding" replace />;
    }

    if (user?.role === 'super_admin') return <Navigate to="/homly-internal" replace />;
    if (user?.role === 'group_manager') return <Navigate to="/group" replace />;
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'agent') return <Navigate to="/agent" replace />;
    if (user?.role === 'seller') return <Navigate to="/seller" replace />;
    return <Navigate to="/login" replace />;
}

function AppRouter() {
    const location = useLocation();

    // CRITICAL: Check URL fragment for session_id synchronously during render
    // This prevents race conditions with OAuth callback
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            {/* Public routes */}
            <Route path="/" element={<WelcomePage />} />
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/agences" element={<LandingPage />} />
            <Route path="/pour-les-agences" element={<PourLesAgencesPage />} />
            <Route path="/pour-les-agents" element={<PourLesAgentsPage />} />
            <Route path="/recherche" element={<RecherchePage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
            <Route path="/diagnostic-agence" element={<DiagnosticPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogArticlePage />} />
            
            {/* Auth routes */}
            <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<Navigate to="/agences?demo=true" replace />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/accept-invite" element={<AcceptInvitePage />} />
                <Route path="/seller/onboarding" element={<SellerOnboardingPage />} />
            </Route>

            {/* Email & Password pages (no layout) */}
            <Route path="/avis/:token" element={<PublicReviewPage />} />
            <Route path="/agents-independants" element={<SoloLandingPage />} />
            <Route path="/solo" element={<SoloLandingPage />} />
            <Route path="/register/solo" element={<RegisterSoloPage />} />
            <Route path="/quickstart" element={<QuickstartPage />} />
            <Route path="/confirm-email" element={<ConfirmEmailPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/resend-confirmation" element={<VerifyEmailPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-personal-email" element={<VerifyPersonalEmailPage />} />

            {/* AI Insights design preview (public mock, no layout, no auth) */}
            <Route path="/preview/ai-insights" element={<AIInsightsPreviewPage />} />

            {/* Onboarding (after OAuth) */}
            <Route path="/onboarding" element={<OnboardingPage />} />
            
            {/* Agency Onboarding (after account creation from CRM) */}
            <Route path="/agency-onboarding" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <AgencyOnboardingPage />
                </ProtectedRoute>
            } />

            {/* Legal routes */}
            <Route path="/cgu" element={<CGUPage />} />
            <Route path="/cgv" element={<CGUPage />} />
            <Route path="/pro" element={<ProRegistrationPage />} />
            <Route path="/pro/login" element={<ProLoginPage />} />
            <Route path="/pro/dashboard" element={<ProDashboardPage />} />
            <Route path="/pro/avis/:token" element={<ProReviewPage />} />
            <Route path="/visite/:token" element={<VisitFormPage />} />
            <Route path="/bon-visite/:token" element={<BonVisiteSignPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/protection-des-donnees" element={<DataProtectionPage />} />
            <Route path="/share/diagnostic/:token" element={<SharedDiagnosticPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />

            {/* Public Profiles (no auth required) */}
            <Route path="/agence/:slug" element={<RedirectToCanonical pattern="/p/agency/:slug" />} />
            <Route path="/profil-agent/:slug" element={<RedirectToCanonical pattern="/p/agent/:slug" />} />

            {/* Réseau Homly - Public routes */}
            <Route path="/reseau" element={<ReseauLandingPage />} />
            <Route path="/reseau/cockpit" element={<ReseauCockpitHubPage />} />
            <Route path="/reseau/profil" element={<ReseauProfilPage />} />
            <Route path="/reseau/statistiques" element={<ReseauStatistiquesPage />} />
            <Route path="/reseau/parametres" element={<ReseauParametresPage />} />
            <Route path="/reseau/recherche" element={<ReseauSearchPage />} />
            <Route path="/reseau/fil" element={<ReseauFeedPage />} />
            <Route path="/reseau/mes-publications" element={<ReseauMyPostsPage />} />
            <Route path="/reseau/ma-reputation" element={<ReseauMyProofsPage />} />
            <Route path="/reseau/avis-vendeurs" element={<ReseauSellerReviewsPage />} />
            <Route path="/reseau/demandes-vendeurs" element={<ReseauSellerIntentsPage />} />
            <Route path="/avis/:token" element={<PublicSellerReviewForm />} />
            <Route path="/reseau/notifications" element={<ReseauNotificationsPage />} />
            <Route path="/p/agent/:slug" element={<PublicAgentProfilePage />} />
            <Route path="/p/agency/:slug" element={<PublicAgencyPage />} />
            <Route path="/reseau/repondre" element={<RespondProposalPage />} />
            <Route path="/p/agence/:slug" element={<RedirectToCanonical pattern="/p/agency/:slug" />} />

            {/* Dossiers notaire — batch 2 P2 */}
            <Route path="/notaire/dossiers" element={<NotaryDossiersListPage />} />
            <Route path="/notaire/dossiers/:id" element={<NotaryDossierDetailPage />} />
            <Route path="/notaire/partage/:token" element={<NotaryPublicViewPage />} />

            {/* Rémunérations & commissions — batch 3 P2 */}
            <Route path="/agence/commissions" element={<CommissionsPage />} />
            <Route path="/agent/commissions" element={<CommissionsPage />} />
            <Route path="/agence/commissions/grilles" element={<CommissionGridsPage />} />
            
            {/* Mandate consultation - Public */}
            <Route path="/mandat/consultation" element={<MandateConsultationPage />} />
            <Route path="/mandat/avenant" element={<AmendmentConsultationPage />} />
            <Route path="/feedback" element={<SellerFeedbackPage />} />
            <Route path="/feedback/:type" element={<SellerFeedbackPage />} />
            
            {/* Certifications - Protected */}
            <Route path="/certifications" element={
                <ProtectedRoute allowedRoles={['admin', 'agent']}>
                    <CertificationsPage />
                </ProtectedRoute>
            } />
            
            {/* Offer signing - Public */}
            <Route path="/offer/sign" element={<OfferSignPage />} />
            <Route path="/buyer-offer/:token" element={<BuyerOfferPublicPage />} />

            {/* Support - Public FAQ + Protected tickets */}
            <Route path="/support" element={<SupportPage />} />
            <Route path="/faq" element={<SupportPage />} />

            {/* Super Admin route */}
            <Route path="/super-admin" element={<SuperAdminPage />} />
            <Route path="/super-admin/stats" element={<SuperAdminStatsPage />} />
            <Route path="/super-admin/qa" element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                    <div className="min-h-screen bg-background p-4 sm:p-6">
                        <QAPanel />
                    </div>
                </ProtectedRoute>
            } />
            <Route path="/super-admin/email-logs" element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                    <div className="min-h-screen bg-background p-4 sm:p-6">
                        <EmailLogsPage />
                    </div>
                </ProtectedRoute>
            } />

            {/* Internal Dashboard - Homly Enterprise */}
            <Route path="/homly-internal" element={<InternalDashboard />} />

            {/* Group Dashboard (multi-agences) */}
            <Route path="/group" element={
                <ProtectedRoute allowedRoles={['group_manager', 'super_admin']}>
                    <GroupDashboardPage />
                </ProtectedRoute>
            } />

            {/* Design Kit interne — preview du Design System "Dark Luxury" */}
            <Route path="/design-kit" element={<DesignKitPage />} />

            {/* Dashboard redirect */}
            <Route path="/dashboard" element={
                <ProtectedRoute>
                    <DashboardRedirect />
                </ProtectedRoute>
            } />

            {/* Admin routes */}
            <Route element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <MainLayout />
                </ProtectedRoute>
            }>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/agency" element={<AgencyDashboard />} />
                <Route path="/admin/offers" element={<OffersManagement isAdmin={true} />} />
                <Route path="/admin/team" element={<TeamPage />} />
                <Route path="/admin/mandates" element={<MandatesPage />} />
                <Route path="/admin/mandates/new" element={<Navigate to="/admin/mandates/upload" replace />} />
                <Route path="/admin/mandates/new-full" element={<Navigate to="/admin/mandates/upload" replace />} />
                <Route path="/admin/mandates/upload" element={<MandateUploadPage />} />
                <Route path="/admin/mandates/my-uploads" element={<MyUploadsPage />} />
                <Route path="/admin/mandates/stats" element={<MandateStatsPage />} />
                <Route path="/admin/mandates/:id" element={<MandateDetailPage />} />
                <Route path="/admin/mandates/v2/:id" element={<MandateDetailV2Page />} />
                <Route path="/admin/estimations" element={<EstimationsPage />} />
                <Route path="/admin/kpi" element={<KPICatalogPage />} />
                <Route path="/admin/ai-report" element={<AIFullReportPage />} />
                <Route path="/admin/targets" element={<TargetsPage />} />
                <Route path="/admin/pilotage" element={<PilotagePage />} />
                <Route path="/admin/simulator" element={<SimulatorPage />} />
                <Route path="/admin/ai-insights" element={<AIInsightsPage />} />
                <Route path="/admin/certifications" element={<CertificationsPage />} />
                <Route path="/admin/billing" element={<BillingPage />} />
                <Route path="/admin/settings" element={<SettingsPage />} />
                <Route path="/admin/notifications" element={<NotificationsPage />} />
                <Route path="/admin/leaderboard" element={<LeaderboardPage />} />
                <Route path="/admin/stats" element={<AgencyStatsPage />} />
                <Route path="/admin/reputation" element={<AgentReputationPage />} />
                <Route path="/admin/profile" element={<ProfilePage />} />
                <Route path="/admin/my-agent-space" element={<AgentCockpitPage />} />
                <Route path="/admin/messaging" element={<MessagingPage />} />
                <Route path="/admin/pros" element={<HomlyProPage />} />
            </Route>

            {/* Agent routes */}
            <Route element={
                <ProtectedRoute allowedRoles={['agent']}>
                    <MainLayout />
                </ProtectedRoute>
            }>
                <Route path="/agent" element={<AgentCockpitPage />} />
                <Route path="/agent/dashboard" element={<AgentDashboard />} />
                <Route path="/agent/mandates" element={<MandatesPage />} />
                <Route path="/agent/mandates/new" element={<Navigate to="/agent/mandates/upload" replace />} />
                <Route path="/agent/mandates/new-full" element={<Navigate to="/agent/mandates/upload" replace />} />
                <Route path="/agent/mandates/upload" element={<MandateUploadPage />} />
                <Route path="/agent/mandates/my-uploads" element={<MyUploadsPage />} />
                <Route path="/agent/mandates/:id" element={<MandateDetailPage />} />
                <Route path="/agent/mandates/v2/:id" element={<MandateDetailV2Page />} />
                <Route path="/agent/estimations" element={<EstimationsPage />} />
                <Route path="/agent/offers" element={<OffersManagement />} />
                <Route path="/agent/notifications" element={<NotificationsPage />} />
                <Route path="/agent/kpi" element={<KPICatalogPage />} />
                <Route path="/agent/leaderboard" element={<LeaderboardPage />} />
                <Route path="/agent/reputation" element={<AgentReputationPage />} />
                <Route path="/agent/studio-partage" element={<ShareStudioPage />} />
                <Route path="/agent/inviter-avis" element={<InviteReviewPage /> } />
                <Route path="/agent/certifications" element={<CertificationsPage />} />
                <Route path="/agent/profile" element={<ProfilePage />} />
                <Route path="/agent/messaging" element={<MessagingPage />} />
                <Route path="/agent/pros" element={<HomlyProPage />} />
            </Route>

            {/* Seller routes */}
            <Route element={
                <ProtectedRoute allowedRoles={['seller']}>
                    <MainLayout />
                </ProtectedRoute>
            }>
                <Route path="/seller" element={<SellerDashboard />} />
                <Route path="/seller/mandates/:id" element={<MandateDetailPage />} />
                <Route path="/seller/notifications" element={<NotificationsPage />} />
                <Route path="/seller/rate/:ratingId" element={<RatingPage />} />
                <Route path="/seller/rate/:ratingId/confirm" element={<ConfirmRatingPage />} />
                <Route path="/seller/support" element={<SupportPage />} />
                <Route path="/seller/messaging" element={<MessagingPage />} />
            </Route>

            {/* 404 Page */}
            <Route path="*" element={<NotFoundPage />} />
        </Routes>
    );
}

// Lot R6 — consolidation des routes publiques dupliquées vers les URL canoniques
function RedirectToCanonical({ pattern }) {
    const { slug } = useParams();
    return <Navigate to={pattern.replace(':slug', slug)} replace />;
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <OfflineProvider>
                    <BrowserRouter>
                        <AppRouter />
                        <Toaster position="top-right" richColors />
                        <InstallBanner />
                    </BrowserRouter>
                </OfflineProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
